import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { TransformControls } from "three/addons/controls/TransformControls.js";
import { useEditor } from "../core/EditorContext";
import { selectEdgeLoop, selectEdgePath, selectEdgeRing, selectFaceLoop } from "../core/mesh";
import type { CommandRequest, MeshData, SelectionState, Vec3 } from "../types";
import { createRenderMesh, selectedComponentCenter, type RenderMeshBundle } from "./geometry";

export interface ViewportApi {
  capture: () => { dataUrl: string; width: number; height: number; view: string };
  frameSelection: () => void;
  frameAll: () => void;
}

interface Props {
  onReady: (api: ViewportApi | null) => void;
}

const deg = (value: number) => value * 180 / Math.PI;

export function Viewport({ onReady }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const { snapshot, setSelection, execute, setTool } = useEditor();
  const snapshotRef = useRef(snapshot);
  const [markingMenu, setMarkingMenu] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => { snapshotRef.current = snapshot; }, [snapshot]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true, powerPreference: "high-performance" });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x0b0d0c, 1);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0b0d0c);
    scene.fog = new THREE.Fog(0x0b0d0c, 900, 2600);
    const perspective = new THREE.PerspectiveCamera(42, 1, 0.1, 10000);
    perspective.position.set(240, 180, 240);
    const top = new THREE.OrthographicCamera(-180, 180, 180, -180, -2000, 2000);
    top.position.set(0, 700, 0);
    top.up.set(0, 0, -1);
    top.lookAt(0, 0, 0);
    const front = new THREE.OrthographicCamera(-180, 180, 180, -180, -2000, 2000);
    front.position.set(0, 0, 700);
    front.lookAt(0, 0, 0);
    const side = new THREE.OrthographicCamera(-180, 180, 180, -180, -2000, 2000);
    side.position.set(700, 0, 0);
    side.lookAt(0, 0, 0);
    scene.add(new THREE.HemisphereLight(0xddeeff, 0x172019, 2.2));
    const key = new THREE.DirectionalLight(0xffffff, 2.8);
    key.position.set(320, 460, 280);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x8fb8ff, 1.25);
    rim.position.set(-260, 180, -320);
    scene.add(rim);
    const grid = new THREE.GridHelper(2000, 100, 0x3e4a43, 0x202721);
    grid.material.transparent = true;
    grid.material.opacity = 0.72;
    scene.add(grid);

    const orbit = new OrbitControls(perspective, renderer.domElement);
    orbit.target.set(0, 0, 0);
    orbit.enableDamping = true;
    orbit.dampingFactor = 0.12;
    orbit.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.DOLLY };
    orbit.enabled = false;
    orbit.update();

    const transform = new TransformControls(perspective, renderer.domElement);
    transform.setSize(0.82);
    const transformHelper = transform.getHelper();
    scene.add(transformHelper);
    const componentTarget = new THREE.Object3D();
    scene.add(componentTarget);
    let bundles: RenderMeshBundle[] = [];
    let altDown = false;
    let animationFrame = 0;
    let activeCamera: THREE.Camera = perspective;
    let dragStartMatrix = new THREE.Matrix4();
    let dragObjectWorld = new THREE.Matrix4();
    let dragObjectId: string | undefined;
    let dragSelection: SelectionState | undefined;
    let rebuilding = false;

    const clearBundles = () => {
      bundles.forEach((bundle) => { scene.remove(bundle.root); bundle.dispose(); });
      bundles = [];
    };

    const updateTransform = () => {
      transform.detach();
      const current = snapshotRef.current;
      const objectId = current.scene.activeObjectId ?? current.selection.objectIds[0];
      const mesh = current.scene.objects.find((candidate) => candidate.id === objectId);
      const bundle = bundles.find((candidate) => candidate.root.userData.objectId === objectId);
      if (!mesh || !bundle || current.tools.tool === "select" || current.tools.tool === "edgeLoop") return;
      transform.setSpace(current.tools.space);
      transform.setMode(current.tools.pivotEditing ? "translate" : current.tools.tool);
      if (current.tools.pivotEditing || current.selection.mode !== "object") {
        const localCenter = current.tools.pivotEditing ? mesh.transform.pivot : selectedComponentCenter(mesh, current.selection);
        componentTarget.position.set(...localCenter).applyMatrix4(bundle.root.matrixWorld);
        componentTarget.rotation.set(0, 0, 0);
        componentTarget.scale.set(1, 1, 1);
        componentTarget.updateMatrixWorld(true);
        transform.attach(componentTarget);
      } else {
        transform.attach(bundle.root);
      }
    };

    const rebuild = () => {
      rebuilding = true;
      clearBundles();
      const current = snapshotRef.current;
      for (const mesh of current.scene.objects) {
        const bundle = createRenderMesh(mesh, current.scene, current.selection, current.tools.display);
        bundles.push(bundle);
        scene.add(bundle.root);
      }
      scene.updateMatrixWorld(true);
      updateTransform();
      rebuilding = false;
    };

    const resize = () => {
      const rect = host.getBoundingClientRect();
      const width = Math.max(1, Math.floor(rect.width));
      const height = Math.max(1, Math.floor(rect.height));
      renderer.setSize(width, height, false);
      perspective.aspect = width / height;
      perspective.updateProjectionMatrix();
      const aspect = width / height;
      [top, front, side].forEach((camera) => {
        camera.left = -180 * aspect;
        camera.right = 180 * aspect;
        camera.top = 180;
        camera.bottom = -180;
        camera.updateProjectionMatrix();
      });
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    resize();

    const renderView = (camera: THREE.Camera, x: number, y: number, width: number, height: number) => {
      renderer.setViewport(x, y, width, height);
      renderer.setScissor(x, y, width, height);
      renderer.setScissorTest(true);
      renderer.render(scene, camera);
    };
    const render = () => {
      animationFrame = requestAnimationFrame(render);
      orbit.update();
      const { width, height } = renderer.getSize(new THREE.Vector2());
      if (snapshotRef.current.tools.quadView) {
        const halfW = Math.floor(width / 2);
        const halfH = Math.floor(height / 2);
        renderView(perspective, 0, halfH, halfW, height - halfH);
        renderView(top, halfW, halfH, width - halfW, height - halfH);
        renderView(front, 0, 0, halfW, halfH);
        renderView(side, halfW, 0, width - halfW, halfH);
      } else {
        renderView(perspective, 0, 0, width, height);
      }
      renderer.setScissorTest(false);
    };
    rebuild();
    render();

    const frameMeshes = (meshes: MeshData[]) => {
      const box = new THREE.Box3();
      bundles.filter((bundle) => meshes.some((mesh) => mesh.id === bundle.root.userData.objectId)).forEach((bundle) => box.expandByObject(bundle.root));
      if (box.isEmpty()) return;
      const sphere = box.getBoundingSphere(new THREE.Sphere());
      const direction = perspective.position.clone().sub(orbit.target).normalize();
      orbit.target.copy(sphere.center);
      perspective.position.copy(sphere.center).add(direction.multiplyScalar(Math.max(120, sphere.radius * 3.2)));
      orbit.update();
    };
    const api: ViewportApi = {
      capture: () => ({ dataUrl: renderer.domElement.toDataURL("image/png"), width: renderer.domElement.width, height: renderer.domElement.height, view: snapshotRef.current.tools.quadView ? "quad" : "perspective" }),
      frameSelection: () => {
        const current = snapshotRef.current;
        frameMeshes(current.scene.objects.filter((mesh) => current.selection.objectIds.includes(mesh.id)));
      },
      frameAll: () => frameMeshes(snapshotRef.current.scene.objects),
    };
    onReady(api);

    const cameraAndNdc = (event: PointerEvent): { camera: THREE.Camera; ndc: THREE.Vector2 } => {
      const rect = renderer.domElement.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      if (!snapshotRef.current.tools.quadView) return { camera: perspective, ndc: new THREE.Vector2(x / rect.width * 2 - 1, -(y / rect.height) * 2 + 1) };
      const right = x >= rect.width / 2;
      const bottom = y >= rect.height / 2;
      activeCamera = !right && !bottom ? perspective : right && !bottom ? top : !right && bottom ? front : side;
      const localX = right ? x - rect.width / 2 : x;
      const localY = bottom ? y - rect.height / 2 : y;
      return { camera: activeCamera, ndc: new THREE.Vector2(localX / (rect.width / 2) * 2 - 1, -(localY / (rect.height / 2)) * 2 + 1) };
    };

    const pick = (event: PointerEvent) => {
      if (event.button !== 0 || event.altKey || transform.dragging) return;
      const current = snapshotRef.current;
      const { camera, ndc } = cameraAndNdc(event);
      const raycaster = new THREE.Raycaster();
      raycaster.params.Line = { threshold: 3 };
      raycaster.params.Points = { threshold: 5 };
      raycaster.setFromCamera(ndc, camera);
      const activeId = current.scene.activeObjectId ?? current.selection.objectIds[0];
      const targets: THREE.Object3D[] = [];
      for (const bundle of bundles) {
        if (current.selection.mode === "object") targets.push(bundle.surface);
        else if (bundle.root.userData.objectId === activeId) {
          if (current.selection.mode === "face") targets.push(bundle.surface);
          if (current.selection.mode === "edge") targets.push(bundle.edges);
          if (current.selection.mode === "vertex") targets.push(bundle.vertices);
        }
      }
      const hit = raycaster.intersectObjects(targets, false)[0];
      if (!hit) {
        if (!event.shiftKey && !event.ctrlKey) setSelection({ ...current.selection, componentIds: [], objectIds: [] });
        return;
      }
      const objectId = hit.object.userData.objectId as string;
      if (current.selection.mode === "object") {
        const objectIds = event.shiftKey
          ? [...new Set([...current.selection.objectIds, objectId])]
          : event.ctrlKey
            ? current.selection.objectIds.filter((id) => id !== objectId)
            : [objectId];
        const nextScene = snapshotRef.current.scene;
        nextScene.activeObjectId = objectId;
        setSelection({ mode: "object", objectIds, componentIds: [] });
        return;
      }
      let componentId: number | undefined;
      if (current.selection.mode === "face") componentId = hit.object.userData.faceIds?.[hit.faceIndex ?? -1];
      if (current.selection.mode === "edge") componentId = hit.object.userData.edgeIds?.[hit.index ?? -1];
      if (current.selection.mode === "vertex") componentId = hit.object.userData.vertexIds?.[hit.index ?? -1];
      if (componentId === undefined) return;
      const mesh = current.scene.objects.find((candidate) => candidate.id === objectId)!;
      let componentIds = current.selection.componentIds;
      const isDouble = event.detail >= 2;
      if (current.selection.mode === "edge" && isDouble) {
        if (event.shiftKey && current.selection.seedComponentId !== undefined) {
          const ring = selectEdgeRing(mesh, current.selection.seedComponentId);
          componentIds = ring.includes(componentId)
            ? ring
            : selectEdgePath(mesh, current.selection.seedComponentId, componentId);
        } else {
          componentIds = selectEdgeLoop(mesh, componentId);
        }
      } else if (current.selection.mode === "face" && isDouble && event.shiftKey && current.selection.seedComponentId !== undefined) {
        componentIds = selectFaceLoop(mesh, current.selection.seedComponentId, componentId);
      } else if (event.ctrlKey) {
        componentIds = componentIds.filter((id) => id !== componentId);
      } else if (event.shiftKey) {
        componentIds = [...new Set([...componentIds, componentId])];
      } else {
        componentIds = [componentId];
      }
      setSelection({ mode: current.selection.mode, objectIds: [objectId], componentIds, seedComponentId: isDouble ? current.selection.seedComponentId : componentId });
    };

    const pointerDown = (event: PointerEvent) => {
      if (event.altKey) {
        altDown = true;
        orbit.enabled = true;
        setMarkingMenu(null);
      }
    };
    const pointerUp = () => {
      if (altDown) { altDown = false; orbit.enabled = false; }
    };
    const contextMenu = (event: MouseEvent) => {
      event.preventDefault();
      if (event.altKey) return;
      const rect = host.getBoundingClientRect();
      setMarkingMenu({ x: event.clientX - rect.left, y: event.clientY - rect.top });
    };
    const keyDown = (event: KeyboardEvent) => {
      if (event.key === "Alt") { altDown = true; orbit.enabled = true; }
    };
    const keyUp = (event: KeyboardEvent) => {
      if (event.key === "Alt") { altDown = false; orbit.enabled = false; }
    };
    renderer.domElement.addEventListener("pointerdown", pointerDown);
    renderer.domElement.addEventListener("pointerup", pointerUp);
    renderer.domElement.addEventListener("click", pick);
    renderer.domElement.addEventListener("contextmenu", contextMenu);
    window.addEventListener("keydown", keyDown);
    window.addEventListener("keyup", keyUp);

    transform.addEventListener("mouseDown", () => {
      const current = snapshotRef.current;
      dragSelection = structuredClone(current.selection);
      dragObjectId = current.scene.activeObjectId ?? current.selection.objectIds[0];
      const bundle = bundles.find((candidate) => candidate.root.userData.objectId === dragObjectId);
      if (current.tools.pivotEditing || current.selection.mode !== "object") {
        componentTarget.updateMatrixWorld(true);
        dragStartMatrix.copy(componentTarget.matrixWorld);
        dragObjectWorld.copy(bundle?.root.matrixWorld ?? new THREE.Matrix4());
      }
      orbit.enabled = false;
    });
    transform.addEventListener("mouseUp", () => {
      if (rebuilding || !dragObjectId || !dragSelection) return;
      const current = snapshotRef.current;
      const mesh = current.scene.objects.find((candidate) => candidate.id === dragObjectId);
      const bundle = bundles.find((candidate) => candidate.root.userData.objectId === dragObjectId);
      if (!mesh || !bundle) return;
      if (current.tools.pivotEditing) {
        const local = componentTarget.position.clone();
        bundle.root.worldToLocal(local);
        void execute({ id: crypto.randomUUID(), type: "object.setTransform", args: { objectId: mesh.id, pivot: local.toArray() } });
      } else if (dragSelection.mode === "object") {
        void execute({
          id: crypto.randomUUID(),
          type: "object.setTransform",
          args: {
            objectId: mesh.id,
            position: bundle.root.position.toArray(),
            rotation: bundle.root.rotation.toArray().slice(0, 3),
            scale: bundle.root.scale.toArray(),
          },
        });
      } else {
        componentTarget.updateMatrixWorld(true);
        const deltaWorld = componentTarget.matrixWorld.clone().multiply(dragStartMatrix.clone().invert());
        const localDelta = dragObjectWorld.clone().invert().multiply(deltaWorld).multiply(dragObjectWorld);
        void execute({ id: crypto.randomUUID(), type: "component.transform", args: { objectId: mesh.id, mode: dragSelection.mode, componentIds: dragSelection.componentIds, matrix: localDelta.elements } });
      }
      dragObjectId = undefined;
      dragSelection = undefined;
    });

    const subscription = window.setInterval(() => {
      const marker = renderer.domElement.dataset.sceneMarker;
      const current = snapshotRef.current;
      const nextMarker = `${current.scene.revision}:${current.selection.mode}:${current.selection.objectIds.join(",")}:${current.selection.componentIds.join(",")}:${current.tools.tool}:${current.tools.space}:${current.tools.display}:${current.tools.pivotEditing}`;
      if (marker !== nextMarker) {
        renderer.domElement.dataset.sceneMarker = nextMarker;
        rebuild();
      }
    }, 30);

    return () => {
      onReady(null);
      window.clearInterval(subscription);
      cancelAnimationFrame(animationFrame);
      observer.disconnect();
      renderer.domElement.removeEventListener("pointerdown", pointerDown);
      renderer.domElement.removeEventListener("pointerup", pointerUp);
      renderer.domElement.removeEventListener("click", pick);
      renderer.domElement.removeEventListener("contextmenu", contextMenu);
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
      transform.dispose();
      orbit.dispose();
      clearBundles();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [execute, onReady, setSelection]);

  const menuSelection = (mode: SelectionState["mode"]) => {
    const current = snapshotRef.current;
    setSelection({ mode, objectIds: current.selection.objectIds.length ? current.selection.objectIds : current.scene.activeObjectId ? [current.scene.activeObjectId] : [], componentIds: [] });
    setMarkingMenu(null);
  };

  return (
    <div className="viewport" ref={hostRef} data-testid="viewport">
      <div className="viewport-watermark">FERAL3D <span>Y-UP · CM</span></div>
      {snapshot.tools.quadView && (
        <div className="quad-labels" aria-hidden="true">
          <span>PERSPECTIVE</span><span>TOP</span><span>FRONT</span><span>RIGHT</span>
        </div>
      )}
      {markingMenu && (
        <div className="marking-menu" style={{ left: markingMenu.x, top: markingMenu.y }} role="menu">
          <button onClick={() => menuSelection("vertex")}>VERTEX <kbd>F9</kbd></button>
          <button onClick={() => menuSelection("edge")}>EDGE <kbd>F10</kbd></button>
          <button onClick={() => menuSelection("face")}>FACE <kbd>F11</kbd></button>
          <button onClick={() => menuSelection("object")}>OBJECT <kbd>F8</kbd></button>
          <button onClick={() => { setTool("edgeLoop"); setMarkingMenu(null); }}>INSERT LOOP</button>
        </div>
      )}
    </div>
  );
}

